import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { selectChevron } from '../AnalyticsAdmin.styles';
import ActionPicker, { useActionFavorites } from './ActionPicker';

function formatDayLabel(day) {
  if (!day) return '';
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function buildWindowParams(windowValue, customFrom, customTo) {
  const params = new URLSearchParams();
  if (windowValue === 'custom') {
    if (customFrom) params.set('from', customFrom);
    if (customTo) params.set('to', customTo);
  } else {
    params.set('days', windowValue);
  }
  return params;
}

const fmt = (n) => Number(n || 0).toLocaleString();

// windowValue ('1' | '7' | '30' | 'all' | 'custom') comes from the Analytics top bar.
const FunnelPanel = ({ projectId, fetchJson, windowValue }) => {
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState('');

  const [step1, setStep1] = useState('');
  const [selectedSteps, setSelectedSteps] = useState([]);

  const favoritesState = useActionFavorites(projectId, fetchJson);
  const { favorites } = favoritesState;

  const [result, setResult] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelError, setFunnelError] = useState('');

  const [focusAction, setFocusAction] = useState('');
  const [breakdowns, setBreakdowns] = useState({});

  // Track the most recent request URL for each fetch so we can (a) skip
  // re-firing an identical request and (b) ignore a stale response if a newer
  // one supersedes it. We never abort: every request that starts still runs.
  const latestActionsPath = useRef('');
  const latestFunnelUrl = useRef('');

  const windowKey = `${windowValue}|${customFrom}|${customTo}`;

  // Load available actions when project/time window changes. Debounced so a
  // burst of changes collapses into one request; deduped so re-renders with the
  // same params do not refetch.
  useEffect(() => {
    if (!projectId) return undefined;
    const params = buildWindowParams(windowValue, customFrom, customTo);
    const path = `/api/analytics/projects/${projectId}/funnel/actions?${params.toString()}`;
    if (path === latestActionsPath.current) return undefined;
    const timer = setTimeout(() => {
      latestActionsPath.current = path;
      setActionsLoading(true);
      setActionsError('');
      fetchJson(path)
        .then((data) => {
          if (path !== latestActionsPath.current) return; // superseded
          setActions(Array.isArray(data.actions) ? data.actions : []);
        })
        .catch((err) => {
          if (path !== latestActionsPath.current) return;
          latestActionsPath.current = ''; // allow retry
          setActionsError(err.message || 'Could not load actions');
          setActions([]);
        })
        .finally(() => {
          if (path === latestActionsPath.current || latestActionsPath.current === '') {
            setActionsLoading(false);
          }
        });
    }, 180);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, windowKey, fetchJson]);

  // Keep selections valid against the current action list.
  useEffect(() => {
    const names = new Set(actions.map((a) => a.action));
    setStep1((prev) => (prev && names.has(prev) ? prev : ''));
    setSelectedSteps((prev) => prev.filter((name) => names.has(name)));
  }, [actions]);

  const toggleStep = useCallback((name) => {
    setSelectedSteps((prev) =>
      prev.includes(name) ? prev.filter((value) => value !== name) : [...prev, name]
    );
  }, []);

  // Auto-compute the funnel when the configuration changes. Debounced so
  // toggling several actions fires one request after you stop, not one per
  // click; deduped so identical configs do not refetch. Never aborts in flight.
  useEffect(() => {
    if (!projectId || !step1) {
      latestFunnelUrl.current = '';
      setResult(null);
      return undefined;
    }
    const params = buildWindowParams(windowValue, customFrom, customTo);
    params.set('step1', step1);
    if (selectedSteps.length > 0) params.set('steps', selectedSteps.join(','));
    const url = `/api/analytics/projects/${projectId}/funnel?${params.toString()}`;
    if (url === latestFunnelUrl.current) return undefined;
    const timer = setTimeout(() => {
      latestFunnelUrl.current = url;
      setFunnelLoading(true);
      setFunnelError('');
      fetchJson(url)
        .then((data) => {
          if (url !== latestFunnelUrl.current) return; // superseded by a newer config
          setResult(data);
          setBreakdowns({});
        })
        .catch((err) => {
          if (url !== latestFunnelUrl.current) return;
          latestFunnelUrl.current = ''; // allow retry
          setFunnelError(err.message || 'Could not compute funnel');
          setResult(null);
        })
        .finally(() => {
          if (url === latestFunnelUrl.current || latestFunnelUrl.current === '') {
            setFunnelLoading(false);
          }
        });
    }, 180);
    return () => clearTimeout(timer);
  }, [projectId, step1, selectedSteps, windowKey, fetchJson]); // eslint-disable-line react-hooks/exhaustive-deps

  const cohort = Number(result?.step1?.players || 0);

  // Biggest groups first.
  const sortedSteps = useMemo(
    () => (result?.steps || []).slice().sort((a, b) => b.players - a.players),
    [result]
  );

  // The detail panel always shows one action: the clicked one, else the largest.
  const focusedStep = sortedSteps.find((s) => s.action === focusAction) || sortedSteps[0] || null;
  const focusedName = focusedStep?.action || '';

  // Load the breakdown for the focused action once per funnel result.
  useEffect(() => {
    if (!result || !focusedName || breakdowns[focusedName]) return undefined;
    let cancelled = false;
    setBreakdowns((prev) => ({ ...prev, [focusedName]: { loading: true } }));
    const params = buildWindowParams(windowValue, customFrom, customTo);
    params.set('step1', result.step1.action);
    params.set('action', focusedName);
    fetchJson(`/api/analytics/projects/${projectId}/funnel/breakdown?${params.toString()}`)
      .then((data) => {
        if (!cancelled) setBreakdowns((prev) => ({ ...prev, [focusedName]: data }));
      })
      .catch((err) => {
        if (!cancelled) setBreakdowns((prev) => ({ ...prev, [focusedName]: { error: err.message || 'Could not load the details.' } }));
      });
    return () => {
      cancelled = true;
      // Drop a request that was still loading so it is retried next time.
      setBreakdowns((prev) => (prev[focusedName]?.loading ? (({ [focusedName]: _, ...rest }) => rest)(prev) : prev));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, focusedName]);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);
  const actionsByName = useMemo(() => new Map(actions.map((a) => [a.action, a])), [actions]);
  const compareOptions = useMemo(() => actions.filter((a) => a.action !== step1), [actions, step1]);
  return (
    <Wrap>
      {/* ---------- builder ---------- */}
      <Card style={{ flex: '1 1 20rem', minWidth: 0 }}>
        {windowValue === 'custom' && (
          <>
            <CustomRange>
              <div>
                <FieldLabel>From</FieldLabel>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              </div>
              <div>
                <FieldLabel>To</FieldLabel>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </div>
            </CustomRange>
            <Divider />
          </>
        )}

        {actionsError ? (
          <Notice $error>{actionsError}</Notice>
        ) : actions.length === 0 ? (
          <Notice>
            {actionsLoading
              ? 'Loading actions…'
              : 'No actions recorded for this range yet. Funnel data fills in from new flushes.'}
          </Notice>
        ) : (
          <>
            <FieldLabel>1 · STARTING ACTION {actionsLoading && <Spinner>Loading…</Spinner>}</FieldLabel>
            <Select
              aria-label="Starting action"
              value={step1}
              onChange={(e) => setStep1(e.target.value)}
              style={{ width: '100%', minWidth: 0, marginBottom: '1.375rem' }}
            >
              <option value="">Choose a starting action</option>
              {favorites.length > 0 && (
                <optgroup label="★ Favorites">
                  {favorites.map((name) => actionsByName.get(name)).filter(Boolean).map((a) => (
                    <option key={`fav-${a.action}`} value={a.action}>
                      {a.action} ({fmt(a.players)} players)
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All actions">
                {actions.filter((a) => !favoriteSet.has(a.action)).map((a) => (
                  <option key={a.action} value={a.action}>
                    {a.action} ({fmt(a.players)} players)
                  </option>
                ))}
              </optgroup>
            </Select>

            <SectionHead>
              <FieldLabel style={{ margin: 0 }}>2 · WHAT DID THEY DO NEXT?</FieldLabel>
              <SmallNote>{selectedSteps.length} selected</SmallNote>
            </SectionHead>

            <ActionPicker
              multi
              actions={compareOptions}
              favorites={favoritesState}
              isSelected={(name) => selectedSteps.includes(name)}
              onPick={toggleStep}
              onAddAll={(names) => setSelectedSteps((prev) => [...new Set([...prev, ...names])])}
            />

            <MutedText style={{ marginTop: '1rem' }}>
              Each bar shows the share of starting players who did that action at least once, in any order.
            </MutedText>
          </>
        )}
      </Card>

      {/* ---------- results ---------- */}
      <div style={{ flex: '999 1 32rem', minWidth: 0, display: 'grid', gap: '1.25rem' }}>
        {funnelError && (
          <Card>
            <Notice $error>{funnelError}</Notice>
          </Card>
        )}

        {!result && !funnelError && (
          <Card>
            <Notice style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
              {funnelLoading ? 'Calculating…' : 'Pick a starting action to see where players go next.'}
            </Notice>
          </Card>
        )}

        {result && !funnelError && (
          <Card>
            <ResultHeader>
              <div>
                <CohortNumber>{cohort.toLocaleString()}</CohortNumber>
                <CohortLabel>
                  players did <strong>{result.step1.action}</strong>
                </CohortLabel>
              </div>
              <RangeTag>
                {funnelLoading
                  ? 'Updating…'
                  : `${formatDayLabel(result.from) || 'Start'} to ${formatDayLabel(result.to) || 'today'}`}
              </RangeTag>
            </ResultHeader>

            {sortedSteps.length === 0 ? (
              <SmallNote style={{ display: 'block', marginTop: '1rem' }}>
                Select one or more actions on the left to compare.
              </SmallNote>
            ) : (
              <ResultSplit>
                <FunnelList aria-label="Compared actions">
                  {sortedSteps.map((step) => {
                    const on = step.action === focusedName;
                    const repeated = step.totalEvents > step.players;
                    return (
                      <li key={step.action}>
                        <FunnelRow type="button" $on={on} aria-pressed={on} onClick={() => setFocusAction(step.action)}>
                          <FunnelTop>
                            <ActionName>{step.action}</ActionName>
                            <Pct>{step.pct}%</Pct>
                          </FunnelTop>
                          <BarTrack>
                            <BarFill style={{ width: `${Math.max(1.5, Math.min(100, step.pct))}%` }} />
                          </BarTrack>
                          <FunnelBottom>
                            <span>{fmt(step.players)} of {fmt(cohort)} players</span>
                            <RepeatTag>{repeated ? `${step.repeatedPct}% repeat` : 'once each'}</RepeatTag>
                          </FunnelBottom>
                        </FunnelRow>
                      </li>
                    );
                  })}
                </FunnelList>

                {focusedStep && (
                  <ActionDetail step={focusedStep} cohort={cohort} data={breakdowns[focusedName]} />
                )}
              </ResultSplit>
            )}
          </Card>
        )}
      </div>
    </Wrap>
  );
};

const ActionDetail = ({ step, cohort, data }) => {
  const distribution = Array.isArray(data?.distribution) ? data.distribution : [];
  const reasons = Array.isArray(data?.reasons) ? data.reasons : [];
  const maxBucket = Math.max(...distribution.map((d) => d.players), 1);
  const avg = Number(step.avgPerPlayer || 1);
  const max = Number(step.maxPerPlayer || 1);

  return (
    <DetailPanel aria-label="Action detail" aria-live="polite">
      <FieldLabel style={{ marginBottom: '0.25rem' }}>SELECTED ACTION</FieldLabel>
      <DetailTitle>{step.action}</DetailTitle>

      <StatGrid>
        <div><span>Players</span><b>{fmt(step.players)}</b></div>
        <div><span>Of starting players</span><b>{cohort ? `${step.pct}%` : '-'}</b></div>
        <div><span>Average per player</span><b>{avg.toLocaleString(undefined, { maximumFractionDigits: 1 })}×</b></div>
        <div><span>Most by one player</span><b>{fmt(max)}×</b></div>
      </StatGrid>

      <DetailHeading>How many times per player</DetailHeading>
      {data?.loading ? (
        <SmallNote style={{ display: 'block', marginBottom: '1.375rem' }}>Loading…</SmallNote>
      ) : data?.error ? (
        <Notice $error style={{ marginBottom: '1.375rem' }}>{data.error}</Notice>
      ) : distribution.length === 0 ? (
        <SmallNote style={{ display: 'block', marginBottom: '1.375rem' }}>No data for this range.</SmallNote>
      ) : (
        <Columns
          $count={distribution.length}
          role="img"
          aria-label={distribution
            .map((d) => `${d.times}${d.plus ? '+' : ''} times: ${step.players ? Math.round((d.players / step.players) * 100) : 0}%`)
            .join(', ')}
        >
          {distribution.map((bucket) => {
            const pct = step.players ? Math.round((bucket.players / step.players) * 100) : 0;
            const top = bucket.players === maxBucket;
            return (
              <Column key={`${bucket.times}-${bucket.plus}`}>
                <small>{pct}%</small>
                <span style={{ height: `${Math.max(2, Math.round((bucket.players / maxBucket) * 100))}px`, opacity: top ? 1 : 0.45 }} />
                <em>{bucket.plus ? `${bucket.times}×+` : `${bucket.times}×`}</em>
              </Column>
            );
          })}
        </Columns>
      )}

      <DetailHeading>By reason</DetailHeading>
      {data && !data.loading && !data.error && reasons.length === 0 ? (
        <SmallNote style={{ display: 'block' }}>
          No reason recorded for this action. The first text field sent with the event is used as its reason.
        </SmallNote>
      ) : (
        <ReasonList>
          {reasons.map((row) => (
            <li key={row.reason}>
              <div>
                <span title={row.reason}>{row.reason}</span>
                <span>
                  <Count style={{ marginRight: '0.5rem' }}>{fmt(row.players)}</Count>
                  {row.pct}%
                </span>
              </div>
              <MiniTrack>
                <ReasonFill style={{ width: `${Math.max(2, Math.min(100, Number(row.pct) || 0))}%` }} />
              </MiniTrack>
            </li>
          ))}
        </ReasonList>
      )}
    </DetailPanel>
  );
};

/* ---------- styles (theme tokens from GlobalStyle) ---------- */

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
`;

const Card = styled.div`
  background: var(--card);
  border-radius: 0.75rem;
  padding: 1.25rem 1.375rem;
  border: 1px solid var(--border);
`;

const MutedText = styled.p`
  color: var(--text-tertiary);
  line-height: 1.55;
  font-size: 0.8125rem;
`;

const Select = styled.select`
  min-width: 180px;
  min-height: 2.75rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-accent);
  background-color: var(--background);
  color: var(--text-primary);
  font-size: 0.875rem;
  ${selectChevron}
`;

const Input = styled.input`
  padding: 0.55rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-accent);
  background: var(--background);
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const FieldLabel = styled.div`
  font-family: var(--font-mono);
  color: var(--text-tertiary);
  font-size: 0.6875rem;
  margin-bottom: 0.5rem;
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
`;

const Spinner = styled.span`
  color: var(--text-tertiary);
  margin-left: 0.5rem;
`;

const CustomRange = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border-subtle);
  margin: 1.125rem 0;
`;

const Notice = styled.div`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--text-secondary)')};
  background: ${(p) => (p.$error ? 'rgba(240, 82, 82, 0.08)' : 'transparent')};
  border-radius: 0.5rem;
  padding: ${(p) => (p.$error ? '0.75rem 0.875rem' : '0.25rem 0')};
  font-size: 0.875rem;
`;

const SmallNote = styled.span`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--text-tertiary)')};
  font-size: 0.8125rem;
`;

const Count = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  color: var(--text-tertiary);
`;

const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const CohortNumber = styled.div`
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1;
`;

const CohortLabel = styled.div`
  color: var(--text-secondary);
  margin-top: 0.375rem;

  strong {
    color: var(--text-primary);
  }
`;

const RangeTag = styled.div`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;

const ResultSplit = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
`;

const FunnelList = styled.ol`
  list-style: none;
  flex: 1 1 17.5rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FunnelRow = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  text-align: left;
  color: var(--text-primary);
  border: 1px solid ${(p) => (p.$on ? 'var(--border-accent)' : 'transparent')};
  background: ${(p) => (p.$on ? 'var(--surface-3)' : 'transparent')};
  box-shadow: ${(p) => (p.$on ? 'inset 3px 0 0 var(--brand)' : 'none')};

  &:hover {
    background: var(--surface-3);
  }
`;

const FunnelTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ActionName = styled.span`
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Pct = styled.span`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.125rem;
`;

const BarTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #1a1d22;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: var(--brand);
  border-radius: 4px;
`;

const FunnelBottom = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;

const RepeatTag = styled.span`
  font-family: var(--font-mono);
  white-space: nowrap;
`;

const DetailPanel = styled.aside`
  flex: 1 1 18.75rem;
  min-width: 0;
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 0.625rem;
  padding: 1.125rem;
`;

const DetailTitle = styled.h3`
  font-size: 1.375rem;
  margin-bottom: 1rem;
  overflow-wrap: anywhere;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  background: var(--border);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1.375rem;

  div {
    background: var(--card);
    padding: 0.625rem 0.75rem;
  }

  span {
    display: block;
    font-size: 0.6875rem;
    color: var(--text-tertiary);
  }

  b {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const DetailHeading = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 0.625rem;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 0.5rem;
  align-items: end;
  margin-bottom: 1.375rem;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  height: 9.5rem;

  small {
    font-size: 0.75rem;
    color: var(--text-code);
  }

  span {
    display: block;
    width: 100%;
    max-width: 3.5rem;
    border-radius: 4px 4px 0 0;
    background: var(--info);
  }

  em {
    font-style: normal;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--text-tertiary);
  }
`;

const ReasonList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  li > div {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.8125rem;
    margin-bottom: 0.25rem;
  }

  li > div > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  li > div > span:last-child {
    color: var(--text-secondary);
    white-space: nowrap;
  }
`;

const MiniTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: #1a1d22;
  overflow: hidden;
`;

const ReasonFill = styled.div`
  height: 100%;
  background: var(--brand);
`;

export default FunnelPanel;
